import { Injectable } from '@angular/core';

import { Observable, BehaviorSubject, combineLatest, distinctUntilChanged, map } from 'rxjs';

import { PageCriteriaModelInterface } from '../../../../../../Common/Misc/models/page-criteria.model.interface';
import { TargetingSearchesApiService } from '../../../../../../api/targeting/targeting-searches.api.service';
import { TargetingListsApiService } from '../../../../../../api/targeting/targeting-lists.api.service';
import { LocalizedEnum } from '../../../../../../api/journodex/model/localized-enum';
import { FilterTypeModel } from '../../../../../../Common/Filter/models/abstract-filter-type.model';
import { JournodexSubjectsService } from '../../../../../../Common/Journodex/services/journodex-subjects.service';
import { JournodexEnumsService } from '../../../../../Core/services/journodex-enums.service';
import { LocalizationService } from '../../../../../../Core/Localization/services/localization.service';
import { TargetingSearchService } from '../../../../services/targeting-search.service';
import { OutletsPublicApi } from '../../../../../../api/targeting/outlets-public.api';
import { BaseSearchService } from '../../../../services/base-search.service';
import { RecipientSearchModel } from './recipient-search.model';
import { DiscoverRoutingService } from '../../../../../DiscoverRouter/services/discover-routing.service';
import { DistributionApi } from '../../../../../../api/distributions/distribution.api';
import { DistributionReportStat } from '../../../../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import { JournodexRegionsService } from '../../../../../../Common/Journodex/services/journodex-regions.service';
import { RecipientResultModel } from '../../../../models/recipient.result.model';
import { RecipientSearchResultsApiModel } from '../../../../../../api/distributions/model/recipient-search-results.api.model';
import { TargetingResultFactory } from '../../../../factories/targeting-result.factory';
import { promiseOnlyResolved } from '../../../../../../Core/utils';
import { UserPreferencesService } from '../../../../../../Account/UserPreferences/services/user-preferences.service';
import { CurrentUserStateService } from '../../../../../../Core/Users/services/current-user-state.service';
import { SortOrderEnum } from '../../../../../../api/shared/enums/sort-order.enum';

@Injectable()
export class AdditionalRecipientSearchService extends BaseSearchService {
    sortFields: LocalizedEnum[] = [
        {
            enumValue: 'EMAIL',
            displayString: 'Discover.targeting.sortFields.email'
        }
    ];

    recipientSearch: RecipientSearchModel = new RecipientSearchModel();
    distributionId: number;

    pageSize: number = 25;

    protected results:  RecipientResultModel[] = null;
    protected results$: BehaviorSubject<RecipientResultModel[]> = new BehaviorSubject<RecipientResultModel[]>(this.results);

    protected displayResults:  RecipientResultModel[] = [];
    protected displayResults$: BehaviorSubject<RecipientResultModel[]> = new BehaviorSubject<RecipientResultModel[]>(this.displayResults);

    constructor(
        protected targetingSearchesApiService: TargetingSearchesApiService,
        protected targetingListsApiService: TargetingListsApiService,
        protected subjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected journodexRegionService: JournodexRegionsService,
        protected localizationService: LocalizationService,
        protected targetingSearchService: TargetingSearchService,
        protected outletsApi: OutletsPublicApi,
        protected discoverRoutingService: DiscoverRoutingService,
        protected distributionApi: DistributionApi,
        protected targetingResultFactory: TargetingResultFactory,
        protected userPreferencesService: UserPreferencesService,
        protected currentUserStateService: CurrentUserStateService
    ) {
        super(
            targetingSearchesApiService,
            targetingListsApiService,
            subjectsService,
            journodexEnumsService,
            journodexRegionService,
            localizationService,
            targetingResultFactory,
            currentUserStateService,
            distributionApi
        );

        this.subscriptions.push(
            combineLatest([
                this.discoverRoutingService.getCurrentRoute().pipe(
                    distinctUntilChanged((pre, curr) => pre.value === curr.value)),
                this.userPreferencesService.getUserAppPreferences().pipe(
                    map (userPreference => userPreference.targetingPrefs.savedSearchesPrefs.defaultDisplaySettings)),
            ]).subscribe(([route, userPreference]) => {
                this.distributionId = +route.value;
                this.pageCriteria.pageSize = userPreference.pageSize;
                this.setPageCriteria(this.pageCriteria, false);
            })
        );
        this.setFilters(this.allFilters);
        this.targetingSortFields$.next(this.sortFields);
        this.sortField = this.sortFields[0];
        this.sortField$.next(this.sortFields[0]);
        this.setPageCriteria({page: 1, pageSize: 25}, false);
    }

    getFilterDefinitions(): FilterTypeModel[] {
        return [
            this.emailFilter
        ];
    }

    /**
     * handle sort of additional recipients
     */
    private handleSort(): void {
        if (this.sortOrder === SortOrderEnum.ascending) {
            this.results.sort((a, b) => a.handles[0].value.toLocaleLowerCase().localeCompare(b.handles[0].value.toLocaleLowerCase()));
        } else {
            this.results.sort((a, b) => b.handles[0].value.toLocaleLowerCase().localeCompare(a.handles[0].value.toLocaleLowerCase()));
        }
    }

    /**
     * Applies the current filters
     */
    applyFilters(): void {
        super.applyFilters();
        this.doSearch();
    }

    /**
     * handle filter of additional recipients
     */
    private handleFilter(): void {
        const emailFilterValues = this.emailFilter.getValue();
        if (emailFilterValues?.length > 0) {
            this.results = this.results.filter(result => {
                let containKeyword = true;
                emailFilterValues.forEach(keyword => {
                    if (!result.handles[0].value.toLowerCase().includes(keyword.toLowerCase())) {
                        containKeyword = false;
                    }
                });
                return containKeyword;
            });
        }
    }

    setStatType(statType: DistributionReportStat) {
        this.recipientSearch.statType = statType;
        this.doSearch();
    }

    getItemCount(): Observable<number> {
        return this.nResults$;
    }

    public setMaxPageCriteria(totalNumberOfPages: number): void {
        this.totalNumberOfPages = totalNumberOfPages;
    }

    /**
     * Updates the page criteria
     */
    setPageCriteria(pageCriteria: PageCriteriaModelInterface, search: boolean = true): void {
        this.page = pageCriteria.page;
        if (this.page <= this.totalNumberOfPages) {
            this.pageCriteria = pageCriteria;
            this.pageCriteria$.next(pageCriteria);
            if (search) {
                this.doSearch();
            }
        }
    }

    getDisplayResults(): Observable<RecipientResultModel[]> {
        return this.displayResults$;
    }

    async setResults(response: RecipientSearchResultsApiModel) {
        const resultPromises = response.recipients.map(
            async x => {
                const result = await this.targetingResultFactory.createRecipientResult(x);
                result.isAdditionalRecipient = true;
                // @HACK NEP-6344 remove this after BE implements email decoder
                if (!result.firstName && !result.lastName) {
                    const email = result.handles[0].value;
                    result.firstName = email.substring(0, email.indexOf('@'));
                }
                return result;
            }
        );
        this.results = await promiseOnlyResolved(resultPromises);
        this.handleSort();
        this.handleFilter();
        this.nResults = this.results.length;
        this.nResults$.next(this.nResults);
        this.displayResults = this.results?.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
        this.displayResults$.next(this.displayResults);
        this.searchLoading$.next(false);
    }

    getSearchLoading(): Observable<boolean> {
        return this.searchLoading$;
    }

    doSearch(): void {
        if (!this.distributionId) {
            return;
        }

        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
        }

        this.searchLoading$.next(true);
        this.mainSearchRequest = this.distributionApi.performAdditionalRecipientSearch(
            this.distributionId,
            this.recipientSearch
        ).subscribe(async result => {
            await this.setResults(result);
        });
    }
}
